import { ReactNode, useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import {
  findCitationInTranscript,
  type HighlightedCitation,
  type OfficeUser,
  type Person,
  type TranscriptTimeDataMapping,
} from '@frontend/api-call-intel';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { ChipVariants, Text, useTooltip } from '@frontend/design-system';
import { variantBackgroundMap } from '../constants';
import { callIntelUtils } from '../utils/call-intel-utils';

export type TranscriptCitation = {
  popoverLabel: string;
  text: string;
  time?: string;
  variant: ChipVariants;
};

type ResolvedTranscriptCitation = TranscriptCitation & {
  start: number;
  end: number;
};

type CitationHighlightProps = {
  children: ReactNode;
  backgroundColor: string;
  citationKeys?: string[];
  citationTime?: string;
  popoverLabel?: string;
};

type HighlightCitationsParams = {
  citations: TranscriptCitation[];
  time: string;
  timeDataMapping?: TranscriptTimeDataMapping;
  transcript: string;
};

type Props = {
  caller?: Person;
  citations?: TranscriptCitation[];
  highlightedCitation?: HighlightedCitation;
  officeUser?: OfficeUser | null;
  speaker: string;
  text: string;
  time: string;
  timeDataMapping?: TranscriptTimeDataMapping;
};

const extractBackground = (variant: ChipVariants): string => variantBackgroundMap[variant] ?? 'transparent';

/**
 * Renders one visual highlight segment with its own tooltip.
 *
 * Tooltips are scoped to the exact highlighted segment rather than the
 * entire transcript row. This is important for overlapping citations,
 * where only the overlapping text should display all applicable labels.
 */
const CitationHighlight = ({
  children,
  backgroundColor,
  citationKeys = [],
  citationTime,
  popoverLabel,
}: CitationHighlightProps) => {
  const { Tooltip, triggerProps, tooltipProps } = useTooltip({
    placement: 'top',
  });

  return (
    <>
      <span
        {...triggerProps}
        className='highlight'
        style={{
          backgroundColor,
        }}
        data-citation-time={citationTime ?? ''}
        data-citation-keys={citationKeys.join(',')}
      >
        {children}
      </span>

      {popoverLabel && (
        <Tooltip {...tooltipProps}>
          <Text size='medium' color='white'>
            {popoverLabel}
          </Text>
        </Tooltip>
      )}
    </>
  );
};

const mergeDuplicateCitations = (citations: TranscriptCitation[]): TranscriptCitation[] => {
  const merged: TranscriptCitation[] = [];

  citations.forEach((citation) => {
    const existingCitation = merged.find(({ text, time }) => text === citation.text && time === citation.time);

    if (!existingCitation) {
      merged.push({ ...citation });
      return;
    }

    const labels = new Set(existingCitation.popoverLabel.split(', '));
    labels.add(citation.popoverLabel);
    existingCitation.popoverLabel = Array.from(labels).join(', ');
  });

  return merged;
};

const resolveCitationRanges = ({
  citations,
  transcript,
  time,
}: {
  citations: TranscriptCitation[];
  transcript: string;
  time: string;
}): ResolvedTranscriptCitation[] => {
  return citations.flatMap((citation) => {
    if (citation.time && citation.time !== time) {
      return [];
    }

    const { start, end, isFound } = findCitationInTranscript({
      citation: citation.text,
      transcript,
    });

    if (!isFound) {
      return [];
    }

    return [
      {
        ...citation,
        start,
        end,
      },
    ];
  });
};

const getCitationKey = ({ text, time }: Pick<TranscriptCitation, 'text' | 'time'>): string =>
  encodeURIComponent(`${time ?? ''}|${text}`);

/**
 * Returns all unique labels that apply to one rendered transcript segment.
 *
 * An overlap segment can belong to multiple citations, so its tooltip needs
 * to explain every reason represented by that section of text.
 */
const getPopoverLabel = (citations: ResolvedTranscriptCitation[]): string =>
  Array.from(new Set(citations.flatMap(({ popoverLabel }) => popoverLabel.split(', ').filter(Boolean)))).join(', ');

/**
 * Chooses which citation controls the color of an overlapping segment.
 *
 * The shortest range is treated as the most specific citation, so a smaller
 * citation nested inside a broader one keeps its more precise visual style.
 * All labels are still preserved in the tooltip regardless of which color wins.
 */
const getMostSpecificCitation = (citations: ResolvedTranscriptCitation[]): ResolvedTranscriptCitation => {
  return citations.reduce((mostSpecific, citation) => {
    const currentLength = citation.end - citation.start;
    const mostSpecificLength = mostSpecific.end - mostSpecific.start;
    return currentLength < mostSpecificLength ? citation : mostSpecific;
  });
};

/**
 * Citation rendering is now interval-based.
 * Every segment gets:
 * - the correct visual color
 * - the labels applicable to that exact segment
 * - references to every citation that owns it
 */
const highlightCitations = ({
  transcript,
  citations,
  time,
  timeDataMapping,
}: HighlightCitationsParams): JSX.Element[] => {
  const resolvedCitations = resolveCitationRanges({
    citations,
    transcript,
    time,
  });

  const boundaries = Array.from(
    new Set([0, transcript.length, ...resolvedCitations.flatMap(({ start, end }) => [start, end])])
  ).sort((a, b) => a - b);

  const elements: JSX.Element[] = [];

  for (let index = 0; index < boundaries.length - 1; index += 1) {
    const start = boundaries[index];
    const end = boundaries[index + 1];

    if (start === end) {
      continue;
    }

    const segmentText = transcript.slice(start, end);

    /**
     * Determine every citation covering this exact segment.
     */
    const activeCitations = resolvedCitations.filter((citation) => citation.start < end && citation.end > start);

    /**
     * No citation covers the segment.
     */
    if (!activeCitations.length) {
      elements.push(<span key={`text-${start}-${end}`}>{segmentText}</span>);

      continue;
    }

    const mostSpecificCitation = getMostSpecificCitation(activeCitations);

    const popoverLabel = getPopoverLabel(activeCitations);

    const citationKeys = activeCitations.map(getCitationKey);

    elements.push(
      <CitationHighlight
        key={`highlight-${start}-${end}`}
        backgroundColor={extractBackground(mostSpecificCitation.variant)}
        citationTime={mostSpecificCitation.time}
        citationKeys={citationKeys}
        popoverLabel={popoverLabel}
      >
        {segmentText}
      </CitationHighlight>
    );
  }

  if (timeDataMapping && timeDataMapping[time]) {
    const { hightlightColor, popoverLabel } = timeDataMapping[time];

    return [
      <CitationHighlight
        key={`time-highlight-${time}`}
        backgroundColor={hightlightColor}
        citationTime={time}
        popoverLabel={popoverLabel}
      >
        {elements}
      </CitationHighlight>,
    ];
  }

  return elements;
};

export const TranscriptCard = ({
  caller,
  citations = [],
  highlightedCitation,
  officeUser,
  speaker,
  text,
  time,
  timeDataMapping,
}: Props) => {
  const { t } = useTranslation('call-intelligence');

  const isOfficeUserInactive = speaker === 'office' && callIntelUtils.isUserInactive(officeUser);

  const speakerName = useMemo(() => {
    if (speaker === 'ai_receptionist') {
      return t('AI Receptionist');
    }

    if (speaker === 'office') {
      return `${officeUser?.firstName || ''} ${officeUser?.lastName || ''}`.trim() || speaker;
    }

    return `${caller?.firstName || ''} ${caller?.lastName || ''}`.trim() || speaker;
  }, [speaker, officeUser?.firstName, officeUser?.lastName, caller?.firstName, caller?.lastName, t]);

  /**
   * Exact duplicates are merged first.
   * Distinct contained or partially-overlapping citations remain separate
   * and are handled by interval segmentation below.
   */
  const mergedCitations = useMemo(() => mergeDuplicateCitations(citations), [citations]);

  const content = useMemo(
    () =>
      highlightCitations({
        citations: mergedCitations,
        time,
        timeDataMapping,
        transcript: text,
      }),
    [mergedCitations, time, timeDataMapping, text]
  );

  const highlightRef = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!highlightedCitation?.time || !highlightedCitation.text || !highlightRef.current) {
      return;
    }

    const targetCitationKey = getCitationKey(highlightedCitation);

    /**
     * Overlapping citations can be split across several rendered spans.
     * Scroll to the first segment owned by the citation that was selected.
     */
    const highlightedElement = Array.from(
      highlightRef.current.querySelectorAll<HTMLElement>('[data-citation-keys]')
    ).find((element) => {
      const keys = element.getAttribute('data-citation-keys')?.split(',').filter(Boolean) ?? [];

      return keys.includes(targetCitationKey);
    });

    if (highlightedElement) {
      setTimeout(() => {
        highlightedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 300);
    }
  }, [highlightedCitation?.time, highlightedCitation?.text, mergedCitations]);

  return (
    <li css={styles.wrapper}>
      <Text className='time' weight='bold' size='medium'>
        {time}
      </Text>

      <Text className='transcript' size='medium'>
        <strong
          style={{
            color: isOfficeUserInactive ? theme.colors.neutral50 : 'inherit',
          }}
        >
          {speakerName}
        </strong>
        :{' '}
        <span ref={highlightRef}>
          <span>{content}</span>
        </span>
      </Text>
    </li>
  );
};

const styles = {
  wrapper: css`
    column-gap: ${theme.spacing(1)};
    display: flex;

    &:not(:last-of-type) {
      margin-bottom: ${theme.spacing(2)};
    }

    .time {
      color: ${theme.colors.neutral30};
      flex-basis: ${theme.spacing(7)};
    }

    .transcript {
      flex: 1;

      strong {
        text-transform: capitalize;
      }
    }

    .highlight {
      cursor: pointer;
    }
  `,
};
